import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/toast";
import { RouteErrorBoundary } from "@/components/ui/route-error-boundary";
import { useAuthStore } from "@/stores/auth-store";

import { AuthLayout } from "@/layouts/AuthLayout";
import { AppLayout } from "@/layouts/AppLayout";
import { AdminLayout } from "@/layouts/AdminLayout";
import { SystemLayout } from "@/layouts/SystemLayout";

import LoginPage from "@/pages/auth/login";
import MfaPage from "@/pages/auth/mfa";
import TenantSelectPage from "@/pages/auth/tenant-select";
import PasskeyEnrollmentPage from "@/pages/auth/passkey-enrollment";
import SessionExpiredPage from "@/pages/auth/session-expired";

import HomePage from "@/pages/enterprise/home";
import WalletsPage from "@/pages/enterprise/wallets";
import WalletDetailPage from "@/pages/enterprise/wallet-detail";
import WalletNewPage from "@/pages/enterprise/wallet-new";
import TransfersPage from "@/pages/enterprise/transfers";
import TransferDetailPage from "@/pages/enterprise/transfer-detail";
import TransferNewPage from "@/pages/enterprise/transfer-new";
import ApprovalsPage from "@/pages/enterprise/approvals";
import ApprovalDetailPage from "@/pages/enterprise/approval-detail";
import ApprovalEscalatePage from "@/pages/enterprise/approval-escalate";
import DeveloperPage from "@/pages/enterprise/developer";
import TreasuryPage from "@/pages/enterprise/treasury";
import TreasuryStakingPage from "@/pages/enterprise/treasury-staking";
import TreasuryStakingNewPage from "@/pages/enterprise/treasury-staking-new";
import TreasuryLendingPage from "@/pages/enterprise/treasury-lending";
import TreasuryLiquidityPage from "@/pages/enterprise/treasury-liquidity";
import TreasuryRoutesPage from "@/pages/enterprise/treasury-routes";
import TreasuryOpportunitiesPage from "@/pages/enterprise/treasury-opportunities";
import GovernancePage from "@/pages/enterprise/governance";
import GovernanceDetailPage from "@/pages/enterprise/governance-detail";
import GovernanceEvaluationPage from "@/pages/enterprise/governance-evaluation";
import MonitoringPage from "@/pages/enterprise/monitoring";
import MonitoringAlertsPage from "@/pages/enterprise/monitoring-alerts";
import MonitoringChainPage from "@/pages/enterprise/monitoring-chain";
import MonitoringProviderPage from "@/pages/enterprise/monitoring-provider";
import MonitoringBroadcastPage from "@/pages/enterprise/monitoring-broadcast";
import MonitoringFreshnessPage from "@/pages/enterprise/monitoring-freshness";
import ReconciliationPage from "@/pages/enterprise/reconciliation";
import ReconciliationExceptionsPage from "@/pages/enterprise/reconciliation-exceptions";
import EvidencePage from "@/pages/enterprise/evidence";
import EvidenceDetailPage from "@/pages/enterprise/evidence-detail";
import EvidenceExportPage from "@/pages/enterprise/evidence-export";
import EvidencePacketPage from "@/pages/enterprise/evidence-packet";
import RecoveryPage from "@/pages/enterprise/recovery";
import SettingsPage from "@/pages/enterprise/settings";
import DeveloperApiKeysPage from "@/pages/enterprise/developer-api-keys";
import DeveloperWebhooksPage from "@/pages/enterprise/developer-webhooks";
import ReconciliationLedgerComparisonPage from "@/pages/enterprise/reconciliation-ledger-comparison";
import ReconciliationRunHistoryPage from "@/pages/enterprise/reconciliation-run-history";
import RecoveryRequestPage from "@/pages/enterprise/recovery-request";
import RecoveryApprovalPage from "@/pages/enterprise/recovery-approval";
import RecoveryExecutionPage from "@/pages/enterprise/recovery-execution";
import RecoveryBackupPage from "@/pages/enterprise/recovery-backup";
import RecoveryAuditPage from "@/pages/enterprise/recovery-audit";
import TreasuryCalendarPage from "@/pages/enterprise/treasury-calendar";
import TreasuryIntentPage from "@/pages/enterprise/treasury-intent";
import TreasuryLendingBorrowPage from "@/pages/enterprise/treasury-lending-borrow";
import TreasuryLendingSupplyPage from "@/pages/enterprise/treasury-lending-supply";
import TreasuryLendingPositionPage from "@/pages/enterprise/treasury-lending-position";
import TreasuryLendingIncidentPage from "@/pages/enterprise/treasury-lending-incident";
import TreasuryLiquidityQuotePage from "@/pages/enterprise/treasury-liquidity-quote";
import TreasuryLiquidityIntentPage from "@/pages/enterprise/treasury-liquidity-intent";
import TreasuryLiquidityExecutionPage from "@/pages/enterprise/treasury-liquidity-execution";
import TreasuryLiquidityReadinessPage from "@/pages/enterprise/treasury-liquidity-readiness";
import TreasuryLiquidityIncidentPage from "@/pages/enterprise/treasury-liquidity-incident";
import TreasuryStakingRewardsPage from "@/pages/enterprise/treasury-staking-rewards";
import TreasuryStakingUnstakePage from "@/pages/enterprise/treasury-staking-unstake";
import TreasuryStakingValidatorPage from "@/pages/enterprise/treasury-staking-validator";

import DeploymentPage from "@/pages/admin/deployment";
import DeploymentDetailPage from "@/pages/admin/admin-deployment-detail";
import AdminDeploymentPage from "@/pages/admin/admin-deployment";
import UsersPage from "@/pages/admin/users";
import UserDetailPage from "@/pages/admin/user-detail";
import InviteUserPage from "@/pages/admin/invite";
import AdminOrgPage from "@/pages/admin/org";
import AdminRolesPage from "@/pages/admin/roles";
import AdminGroupsPage from "@/pages/admin/groups";
import AdminServiceAccountsPage from "@/pages/admin/service-accounts";
import AdminAuditLogPage from "@/pages/admin/audit-log";
import AdminAuthConfigPage from "@/pages/admin/auth-config";
import AdminAuthDiagnosticsPage from "@/pages/admin/auth-diagnostics";
import AdminServicesPage from "@/pages/admin/services";
import AdminConfigViewerPage from "@/pages/admin/config-viewer";
import AdminSupportBundlePage from "@/pages/admin/support-bundle";

import SystemStatesPage from "@/pages/system/system-states";
import SystemLoadingPage from "@/pages/system/loading";
import SystemEmptyPage from "@/pages/system/empty";
import SystemMaintenancePage from "@/pages/system/maintenance";
import SystemNotFoundPage from "@/pages/system/not-found";
import SystemOutcomeUnknownPage from "@/pages/system/outcome-unknown";
import SystemPermissionDeniedPage from "@/pages/system/permission-denied";
import SystemStalePage from "@/pages/system/stale";
import RouteNotFoundPage from "@/pages/system/route-not-found";

const AUTH_PATHS = ["/login", "/mfa", "/tenant-select", "/passkey-enrollment", "/session-expired"];

function RequireAuth({ children }: { children: React.ReactNode }) {
  const step = useAuthStore((s) => s.step);
  const location = useLocation();

  if (step === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg">
        <div className="spinner" aria-label="Checking session" />
      </div>
    );
  }

  if (step === "app" && AUTH_PATHS.includes(location.pathname)) {
    return <Navigate to="/wallets" replace />;
  }

  if (step === "login" && !AUTH_PATHS.includes(location.pathname)) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export function App() {
  const initSession = useAuthStore((s) => s.initSession);

  useEffect(() => {
    initSession();
  }, [initSession]);

  return (
    <RequireAuth>
      <Toaster />
      <RouteErrorBoundary>
        <Routes>
          {/* Auth routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/mfa" element={<MfaPage />} />
            <Route path="/tenant-select" element={<TenantSelectPage />} />
            <Route path="/passkey-enrollment" element={<PasskeyEnrollmentPage />} />
          </Route>
          <Route path="/session-expired" element={<SessionExpiredPage />} />

          {/* Enterprise wallet routes */}
          <Route element={<AppLayout variant="enterprise" />}>
            <Route path="/home" element={<HomePage />} />
            <Route path="/wallets" element={<WalletsPage />} />
            <Route path="/wallets/:id" element={<WalletDetailPage />} />
            <Route path="/wallets/new" element={<WalletNewPage />} />
            <Route path="/transfers" element={<TransfersPage />} />
            <Route path="/transfers/:id" element={<TransferDetailPage />} />
            <Route path="/transfers/new" element={<TransferNewPage />} />
            <Route path="/approvals" element={<ApprovalsPage />} />
            <Route path="/approvals/:id" element={<ApprovalDetailPage />} />
            <Route path="/approvals/:id/escalate" element={<ApprovalEscalatePage />} />
            <Route path="/developer" element={<DeveloperPage />} />
            <Route path="/treasury" element={<TreasuryPage />} />
            <Route path="/treasury/staking" element={<TreasuryStakingPage />} />
            <Route path="/treasury/staking/new" element={<TreasuryStakingNewPage />} />
            <Route path="/treasury/lending" element={<TreasuryLendingPage />} />
            <Route path="/treasury/liquidity" element={<TreasuryLiquidityPage />} />
            <Route path="/treasury/routes" element={<TreasuryRoutesPage />} />
            <Route path="/treasury/opportunities" element={<TreasuryOpportunitiesPage />} />
            <Route path="/governance" element={<GovernancePage />} />
            <Route path="/governance/:id" element={<GovernanceDetailPage />} />
            <Route path="/governance/evaluation" element={<GovernanceEvaluationPage />} />
            <Route path="/monitoring" element={<MonitoringPage />} />
            <Route path="/monitoring/alerts" element={<MonitoringAlertsPage />} />
            <Route path="/monitoring/chain" element={<MonitoringChainPage />} />
            <Route path="/monitoring/provider" element={<MonitoringProviderPage />} />
            <Route path="/monitoring/broadcast" element={<MonitoringBroadcastPage />} />
            <Route path="/monitoring/freshness" element={<MonitoringFreshnessPage />} />
            <Route path="/reconciliation" element={<ReconciliationPage />} />
            <Route path="/reconciliation/exceptions" element={<ReconciliationExceptionsPage />} />
            <Route path="/evidence" element={<EvidencePage />} />
            <Route path="/evidence/:id" element={<EvidenceDetailPage />} />
            <Route path="/evidence/export" element={<EvidenceExportPage />} />
            <Route path="/evidence/packet" element={<EvidencePacketPage />} />
            <Route path="/recovery" element={<RecoveryPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/developer/api-keys" element={<DeveloperApiKeysPage />} />
            <Route path="/developer/webhooks" element={<DeveloperWebhooksPage />} />
            <Route path="/reconciliation/ledger-comparison" element={<ReconciliationLedgerComparisonPage />} />
            <Route path="/reconciliation/run-history" element={<ReconciliationRunHistoryPage />} />
            <Route path="/recovery/request" element={<RecoveryRequestPage />} />
            <Route path="/recovery/approval" element={<RecoveryApprovalPage />} />
            <Route path="/recovery/execution" element={<RecoveryExecutionPage />} />
            <Route path="/recovery/backup" element={<RecoveryBackupPage />} />
            <Route path="/recovery/audit" element={<RecoveryAuditPage />} />
            <Route path="/treasury/calendar" element={<TreasuryCalendarPage />} />
            <Route path="/treasury/intent" element={<TreasuryIntentPage />} />
            <Route path="/treasury/lending/borrow" element={<TreasuryLendingBorrowPage />} />
            <Route path="/treasury/lending/supply" element={<TreasuryLendingSupplyPage />} />
            <Route path="/treasury/lending/position" element={<TreasuryLendingPositionPage />} />
            <Route path="/treasury/lending/incident" element={<TreasuryLendingIncidentPage />} />
            <Route path="/treasury/liquidity/quote" element={<TreasuryLiquidityQuotePage />} />
            <Route path="/treasury/liquidity/intent" element={<TreasuryLiquidityIntentPage />} />
            <Route path="/treasury/liquidity/execution" element={<TreasuryLiquidityExecutionPage />} />
            <Route path="/treasury/liquidity/readiness" element={<TreasuryLiquidityReadinessPage />} />
            <Route path="/treasury/liquidity/incident" element={<TreasuryLiquidityIncidentPage />} />
            <Route path="/treasury/staking/rewards" element={<TreasuryStakingRewardsPage />} />
            <Route path="/treasury/staking/unstake" element={<TreasuryStakingUnstakePage />} />
            <Route path="/treasury/staking/validator" element={<TreasuryStakingValidatorPage />} />
          </Route>

          {/* Admin routes */}
          <Route element={<AdminLayout />}>
            <Route path="/admin/deployment" element={<AdminDeploymentPage />} />
            <Route path="/admin/deployment/:id" element={<DeploymentDetailPage />} />
            <Route path="/admin/users" element={<UsersPage />} />
            <Route path="/admin/users/:id" element={<UserDetailPage />} />
            <Route path="/admin/invite" element={<InviteUserPage />} />
            <Route path="/deployment" element={<DeploymentPage />} />
            <Route path="/admin/org" element={<AdminOrgPage />} />
            <Route path="/admin/roles" element={<AdminRolesPage />} />
            <Route path="/admin/groups" element={<AdminGroupsPage />} />
            <Route path="/admin/service-accounts" element={<AdminServiceAccountsPage />} />
            <Route path="/admin/audit-log" element={<AdminAuditLogPage />} />
            <Route path="/admin/auth-config" element={<AdminAuthConfigPage />} />
            <Route path="/admin/auth-diagnostics" element={<AdminAuthDiagnosticsPage />} />
            <Route path="/admin/services" element={<AdminServicesPage />} />
            <Route path="/admin/config-viewer" element={<AdminConfigViewerPage />} />
            <Route path="/admin/support-bundle" element={<AdminSupportBundlePage />} />
          </Route>

          {/* System routes */}
          <Route element={<SystemLayout />}>
            <Route path="/system" element={<SystemStatesPage />} />
            <Route path="/system-states" element={<SystemStatesPage />} />
            <Route path="/system/loading" element={<SystemLoadingPage />} />
            <Route path="/system/empty" element={<SystemEmptyPage />} />
            <Route path="/system/maintenance" element={<SystemMaintenancePage />} />
            <Route path="/system/404" element={<SystemNotFoundPage />} />
            <Route path="/system/outcome-unknown" element={<SystemOutcomeUnknownPage />} />
            <Route path="/system/permission-denied" element={<SystemPermissionDeniedPage />} />
            <Route path="/system/stale" element={<SystemStalePage />} />
          </Route>

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/wallets" replace />} />
          <Route path="*" element={<RouteNotFoundPage />} />
        </Routes>
      </RouteErrorBoundary>
    </RequireAuth>
  );
}
